import React, { useState } from "react";
import { TimePicker } from "antd";
import dayjs from "dayjs";
import { ReactComponent as ClockIcon } from "../../../../Assets/Icons/Clock.svg";
import { ReactComponent as ArrowIcon } from "../../../../Assets/Icons/TimeSuffixIcon.svg";
import Checkbox from "../../../common/Checkbox";

const DayTimeSelector = ({
    timeData,
    setFieldValue,
    format,
    error,
    touched,
}) => {
    const dayAbbreviations = {
        Sunday: "Sun",
        Monday: "Mon",
        Tuesday: "Tue",
        Wednesday: "Wed",
        Thursday: "Thu",
        Friday: "Fri",
        Saturday: "Sat",
    };

    const [specificHours, setSpecificHours] = useState(false);

    const handleDayToggle = (index) => {
        const currentState = timeData[index].isSelected;
        setFieldValue(`timeData.${index}.isSelected`, !currentState);

        if (!currentState) {
            // Set default times when enabling a day
            setFieldValue(`timeData.${index}.openTime`, dayjs("09:00 AM", format));
            setFieldValue(`timeData.${index}.closeTime`, dayjs("05:00 PM", format));
        } else {
            // Clear times when disabling a day
            setFieldValue(`timeData.${index}.openTime`, undefined);
            setFieldValue(`timeData.${index}.closeTime`, undefined);
        }
    };

    const handleDefaultTimeChange = (field, time) => {
        // Apply time to all selected days when not in specific hours mode
        if (!specificHours) {
            timeData.forEach((day, index) => {
                if (day.isSelected) {
                    setFieldValue(`timeData.${index}.${field}`, time);
                }
            });
        }
    };

    const handleSpecificHoursToggle = () => {
        const newState = !specificHours;
        setSpecificHours(newState);

        if (!newState) {
            // Reset all selected days to default times
            timeData.forEach((day, index) => {
                if (day.isSelected) {
                    setFieldValue(`timeData.${index}.openTime`, dayjs("09:00 AM", format));
                    setFieldValue(`timeData.${index}.closeTime`, dayjs("05:00 PM", format));
                }
            });
        }
    };

    const handleIndividualTimeChange = (dayIndex, field, time) => {
        setFieldValue(`timeData.${dayIndex}.${field}`, time);
    };

    const selectedDays = timeData.filter((day) => day.isSelected);
    const firstSelectedDay = selectedDays[0];

    return (
        <div className="day-time-selector">
            {/* Day Buttons */}
            <div className="days-row">
                {timeData.map((day, index) => (
                    <button
                        key={index}
                        type="button"
                        className={`day-btn ${day.isSelected ? "active" : ""}`}
                        onClick={() => handleDayToggle(index)}
                    >
                        {dayAbbreviations[day.day]}
                    </button>
                ))}
            </div>

            {/* Default Time Pickers - Only show when checkbox is NOT checked */}
            {!specificHours && (
                <div className="time-pickers-row">
                    <div className="time-picker-group">
                        <label>Open Time</label>
                        <TimePicker
                            className="time-picker-input"
                            allowClear={false}
                            use12Hours={true}
                            format={format}
                            suffixIcon={<ClockIcon />}
                            value={firstSelectedDay?.openTime}
                            onChange={(time) => handleDefaultTimeChange("openTime", time)}
                            disabled={selectedDays.length === 0}
                        />
                    </div>
                    <div className="time-picker-group">
                        <label>Close Time</label>
                        <TimePicker
                            className="time-picker-input"
                            allowClear={false}
                            use12Hours={true}
                            format={format}
                            suffixIcon={<ClockIcon />}
                            value={firstSelectedDay?.closeTime}
                            onChange={(time) => handleDefaultTimeChange("closeTime", time)}
                            disabled={selectedDays.length === 0}
                        />
                    </div>
                </div>
            )}

            {/* Checkbox */}
            <div className="specific-hours-checkbox">
                <Checkbox
                    text="Check to set specific hours."
                    checked={specificHours}
                    onChecked={handleSpecificHoursToggle}
                    divClassName="flex items-center cursor-pointer select-none"
                    textClass="text-[14px] font-[400] font-poppins text-[#767676] ml-2 cursor-pointer"
                />
            </div>

            {specificHours && selectedDays.length === 0 && (
                <p className="text-[12px] text-[#00ADEF] font-poppins mb-4 animate-pulse">
                    Please select the days above to set their specific hours.
                </p>
            )}

            {/* Conditional Specific Hours Table */}
            {specificHours && (
                <div className="specific-hours-table-section mt-5 border-t border-gray-100 pt-5">
                    {selectedDays.length > 0 ? (
                        <div className="specific-hours-table">
                            <div className="table-header mb-2">
                                <div className="header-cell">Working Days</div>
                                <div className="header-cell">Open Time</div>
                                <div className="header-cell">Close Time</div>
                            </div>
                            {timeData.map((day, index) => {
                                if (!day.isSelected) return null;
                                return (
                                    <div key={index} className="time-row py-3">
                                        <div className="day-label">{day.day}</div>
                                        <div className="time-inputs-container">
                                            <TimePicker
                                                className="time-picker-small"
                                                allowClear={false}
                                                use12Hours={true}
                                                format={format}
                                                showNow={false}
                                                suffixIcon={<ArrowIcon />}
                                                value={day.openTime}
                                                onChange={(time) => handleIndividualTimeChange(index, "openTime", time)}
                                            />
                                            <span className="to-text px-2">To</span>
                                            <TimePicker
                                                className="time-picker-small"
                                                allowClear={false}
                                                use12Hours={true}
                                                format={format}
                                                showNow={false}
                                                suffixIcon={<ArrowIcon />}
                                                value={day.closeTime}
                                                onChange={(time) => handleIndividualTimeChange(index, "closeTime", time)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="mt-4 flex items-center gap-2 opacity-100 select-none cursor-default">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="#00ADEF" strokeWidth="2" />
                                <path d="M12 16V11" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" />
                                <path d="M12 8H12.01" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <p className="text-[13px] text-[#767676] font-poppins font-[400]">
                                Please select the working days from the buttons above to set their specific hours.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DayTimeSelector;
